import type { KeycloakConfig } from 'keycloak-js';
import Keycloak from 'keycloak-js';
import { BffAuthenticatedApiClient } from '@cvo/domain';
import type { Composer } from 'vue-i18n';
import { TOKEN_REFRESH_LEEWAY_SECONDS, CHECK_REFRESH_TOKEN_THRESHOLD_SECONDS } from '@cvo/domain/api/constants';

export default defineNuxtPlugin({
  name: 'keycloak',
  async setup(nuxtApp) {
    const config = useRuntimeConfig();
    const { startWatching, stopWatching } = useAuthRedirectCleaner();
    const isAuthenticated = useState('authenticated', () => false);

    const initOptions: KeycloakConfig = {
      url: `${config.public.keycloakUrl.replace(/\/+$/, '')}/auth`,
      realm: config.public.keycloakRealm,
      clientId: config.public.keycloakClientId,
    };

    const keycloak = new Keycloak(initOptions);

    function updateLoginState() {
      console.debug('[keycloak]', 'Auth token updated, registering in ApiClient.');
      isAuthenticated.value = true;
      if (keycloak.token) {
        BffAuthenticatedApiClient.getInstance().setAuthToken(keycloak.token);
      }
    };

    function updateLogoutState() {
      console.debug('[keycloak]', 'Auth token expired or removed, removing from ApiClient.');
      isAuthenticated.value = false;
      BffAuthenticatedApiClient.getInstance().clearAuthToken();
    };

    // ttl = -1 will force refresh the token immediately
    function refreshToken(ttl: number = TOKEN_REFRESH_LEEWAY_SECONDS) {
      console.debug('[keycloak]', 'Refreshing auth token proactively.');
      return keycloak.updateToken(ttl);
    }

    let refreshInterval: ReturnType<typeof setInterval>;
    function setupRefreshToken() {
      // Set up proactive token refresh (refresh 1 minute before expiration)
      refreshInterval = setInterval(() => {
        if (keycloak.authenticated && keycloak.tokenParsed) {
          refreshToken();
        }
      }, CHECK_REFRESH_TOKEN_THRESHOLD_SECONDS * 1000);

      window.addEventListener('beforeunload', () => {
        clearRefreshTokenWatcher();
      });
    }

    function clearRefreshTokenWatcher() {
      clearInterval(refreshInterval);
    }

    function onLogin() {
      setupRefreshToken();
      updateLoginState();
    }

    function onLogout() {
      updateLogoutState();
      clearRefreshTokenWatcher();
    }

    keycloak.onAuthSuccess = onLogin;
    keycloak.onAuthLogout = onLogout;
    keycloak.onAuthError = onLogout;
    keycloak.onAuthRefreshSuccess = updateLoginState;
    keycloak.onAuthRefreshError = updateLogoutState;
    keycloak.onTokenExpired = refreshToken;

    // Start watching before initialization to catch redirect parameters
    startWatching();

    try {
      await keycloak.init({
        onLoad: 'check-sso',
        locale: (nuxtApp.$i18n as Composer).locale.value,
        redirectUri: window.location.href,
        responseMode: 'query',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      });
    } catch (error) {
      console.error('[keycloak]', 'Initialization "check-sso" mode failed:', error);
      stopWatching();
    }

    return {
      provide: {
        keycloak,
      },
    };
  },
});

declare module '#app' {
  interface NuxtApp {
    $keycloak: Keycloak;
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $keycloak: Keycloak;
  }
}